import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  Megaphone, 
  DollarSign,
  Eye,
  EyeOff,
  Award,
  MessageSquare,
  Truck,
  ShieldCheck,
  Smartphone
} from 'lucide-react';
import { authApi, default as api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/hooks/useToast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';

interface ConfiguracoesData {
  // Custos
  custoKwhAtual: number;
  custoHoraOperador: number;
  horasUteisMes: number;
  custosFixosMensais: number;
  taxaDesperdicioPadrao: number;
  margemMinimaPadrao: number;
  
  // Banner
  bannerAtivo: boolean;
  bannerTexto: string;
  bannerCorFundo: string;
  bannerCorTexto: string;
  bannerLink: string;

  // Frete (Melhor Envio)
  melhorEnvioToken: string;
  melhorEnvioSandbox: boolean;

  // WhatsApp
  whatsappProvider: string;
  whatsappApiUrl: string;
  whatsappApiKey: string;
  whatsappInstanceId: string;

  // Fidelidade
  fidelidadeAtivo: boolean;
  fidelidadePtosPorReal: number;
  fidelidadeValorPonto: number;
  fidelidadeResgateMinimo: number;
  fidelidadeExpiraDias: number;
}

export function Configuracoes() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [config, setConfig] = useState<ConfiguracoesData>({
    custoKwhAtual: 0.8,
    custoHoraOperador: 15,
    horasUteisMes: 200,
    custosFixosMensais: 0,
    taxaDesperdicioPadrao: 5,
    margemMinimaPadrao: 30,
    bannerAtivo: true,
    bannerTexto: '🚚 Frete grátis para compras acima de R$ 150 | 🎁 Cupom PRIMEIRA10 para 10% OFF',
    bannerCorFundo: '#FF6B35',
    bannerCorTexto: '#FFFFFF',
    bannerLink: '',
    melhorEnvioToken: '',
    melhorEnvioSandbox: true,
    whatsappProvider: 'OFFICIAL',
    whatsappApiUrl: '',
    whatsappApiKey: '',
    whatsappInstanceId: '',
    fidelidadeAtivo: true,
    fidelidadePtosPorReal: 1,
    fidelidadeValorPonto: 0.05,
    fidelidadeResgateMinimo: 500,
    fidelidadeExpiraDias: 365
  });

  // 2FA State
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [setup2FAData, setSetup2FAData] = useState<{ secret: string; qrCode: string } | null>(null);
  const [token2fa, setToken2fa] = useState('');
  const [activating, setActivating] = useState(false);
  const [userMe, setUserMe] = useState<any>(null);

  useEffect(() => {
    carregarConfig();
    carregarUsuario();
  }, []);

  const carregarUsuario = async () => {
    try {
      const { data } = await authApi.me();
      setUserMe(data);
    } catch (err) {
      console.warn('Não foi possível carregar dados do usuário (2FA ignorado):', (err as any)?.response?.status);
      // Fallback: ignorar 2FA se não conseguir carregar dados do usuário
      setUserMe({ twoFactorEnabled: false });
    }
  };

  const handleSetup2FA = async () => {
    try {
      const { data } = await authApi.setup2FA();
      setSetup2FAData(data);
      setShow2FAModal(true);
    } catch (err: any) {
      const msg = err?.response?.status === 404 
        ? 'Serviço de 2FA não disponível no servidor. Faça o deploy do backend primeiro.'
        : 'Falha ao iniciar setup 2FA';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    }
  };

  const confirmarAtivacao2FA = async () => {
    try {
      setActivating(true);
      await authApi.ativar2FA(token2fa);
      toast({ title: 'Sucesso', description: '2FA ativado com sucesso!' });
      setShow2FAModal(false);
      carregarUsuario();
    } catch (err: any) {
      toast({ title: 'Erro', description: err.response?.data?.message || 'Token inválido', variant: 'destructive' });
    } finally {
      setActivating(false);
    }
  };

  const carregarConfig = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/configuracoes');
      if (data) {
        setConfig(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/configuracoes', config);
      toast({
        title: 'Sucesso',
        description: 'Configurações salvas com sucesso!',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar as configurações.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#FF6B35]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Settings className="w-7 h-7 text-[#FF6B35]" />
            Configurações
          </h1>
          <p className="text-gray-500 mt-1">
            Personalize banner, custos, integrações e fidelidade
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setPreview(!preview)}>
            {preview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {preview ? 'Ocultar Preview' : 'Ver Preview'}
          </Button>
          <Button variant="nuvem" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      {/* Preview do Banner */}
      {preview && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-gray-100 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-2">Preview do Banner:</p>
            {config.bannerAtivo ? (
              <div
                className="text-xs py-2 px-4 text-center rounded"
                style={{
                  backgroundColor: config.bannerCorFundo,
                  color: config.bannerCorTexto,
                }}
              >
                {config.bannerTexto}
              </div>
            ) : (
              <p className="text-gray-400 text-sm italic">Banner desativado</p>
            )}
          </div>
        </motion.div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Banner Promocional */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Banner Promocional</h2>
              <p className="text-sm text-gray-500">Configure o banner do topo do site</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Banner Ativo
              </label>
              <button
                onClick={() => setConfig({ ...config, bannerAtivo: !config.bannerAtivo })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.bannerAtivo ? 'bg-[#FF6B35]' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.bannerAtivo ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texto do Banner
              </label>
              <textarea
                value={config.bannerTexto}
                onChange={(e) => setConfig({ ...config, bannerTexto: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
                placeholder="🚚 Frete grátis..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cor de Fundo
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={config.bannerCorFundo}
                    onChange={(e) => setConfig({ ...config, bannerCorFundo: e.target.value })}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={config.bannerCorFundo}
                    onChange={(e) => setConfig({ ...config, bannerCorFundo: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cor do Texto
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={config.bannerCorTexto}
                    onChange={(e) => setConfig({ ...config, bannerCorTexto: e.target.value })}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={config.bannerCorTexto}
                    onChange={(e) => setConfig({ ...config, bannerCorTexto: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Programa de Fidelidade (NOVO) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Award className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Fort3D Club</h2>
              <p className="text-sm text-gray-500">Programa de Fidelidade e Recompensa</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Fidelidade Ativa
              </label>
              <button
                onClick={() => setConfig({ ...config, fidelidadeAtivo: !config.fidelidadeAtivo })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  config.fidelidadeAtivo ? 'bg-yellow-500' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    config.fidelidadeAtivo ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                   1 Real vale (Pontos)
                </label>
                <Input
                  type="number"
                  value={config.fidelidadePtosPorReal}
                  onChange={(e) => setConfig({ ...config, fidelidadePtosPorReal: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  1 Ponto vale (R$)
                </label>
                <Input
                  type="number"
                  step="0.001"
                  value={config.fidelidadeValorPonto}
                  onChange={(e) => setConfig({ ...config, fidelidadeValorPonto: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                   Resgate Mínimo (Pts)
                </label>
                <Input
                  type="number"
                  value={config.fidelidadeResgateMinimo}
                  onChange={(e) => setConfig({ ...config, fidelidadeResgateMinimo: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiração (Dias)
                </label>
                <Input
                  type="number"
                  value={config.fidelidadeExpiraDias}
                  onChange={(e) => setConfig({ ...config, fidelidadeExpiraDias: parseInt(e.target.value) })}
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border border-yellow-100 italic">
              Exemplo: Com {config.fidelidadePtosPorReal} pt por real e 1 pt valendo R$ {config.fidelidadeValorPonto}, uma compra de R$ 100 gera {100 * config.fidelidadePtosPorReal} pontos (R$ {(100 * config.fidelidadePtosPorReal * config.fidelidadeValorPonto).toFixed(2)} de cashback).
            </p>
          </div>
        </motion.div>

        {/* Integração WhatsApp */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">WhatsApp</h2>
              <p className="text-sm text-gray-500">Notificações automáticas</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provedor
              </label>
              <Select 
                value={config.whatsappProvider} 
                onValueChange={(val) => setConfig({ ...config, whatsappProvider: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o provedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OFFICIAL">Meta API (Oficial)</SelectItem>
                  <SelectItem value="EVOLUTION">Evolution API (Recomendado)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API URL
              </label>
              <Input
                placeholder="Ex: https://api.exemplo.com"
                value={config.whatsappApiUrl}
                onChange={(e) => setConfig({ ...config, whatsappApiUrl: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key / Token
              </label>
              <Input
                type="password"
                placeholder="Seu token secreto"
                value={config.whatsappApiKey}
                onChange={(e) => setConfig({ ...config, whatsappApiKey: e.target.value })}
              />
            </div>

            {config.whatsappProvider === 'EVOLUTION' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID da Instância
                </label>
                <Input
                  placeholder="Nome da instância no Evolution"
                  value={config.whatsappInstanceId}
                  onChange={(e) => setConfig({ ...config, whatsappInstanceId: e.target.value })}
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* Logística (Melhor Envio) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Truck className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Melhor Envio</h2>
              <p className="text-sm text-gray-500">Cálculo de frete e etiquetas</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Token
                </label>
                <Input
                  type="password"
                  placeholder="Token do Melhor Envio"
                  value={config.melhorEnvioToken}
                  onChange={(e) => setConfig({ ...config, melhorEnvioToken: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-base font-medium text-gray-900">
                    Modo Sandbox
                  </label>
                  <p className="text-sm text-gray-500">Ative para realizar testes sem custos reais</p>
                </div>
                <button
                  onClick={() => setConfig({ ...config, melhorEnvioSandbox: !config.melhorEnvioSandbox })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.melhorEnvioSandbox ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.melhorEnvioSandbox ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Custos Operacionais */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Custos e Precificação</h2>
              <p className="text-sm text-gray-500">Valores globais para ficha técnica</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custo kWh (R$)
              </label>
              <Input
                type="number"
                step="0.01"
                value={config.custoKwhAtual}
                onChange={(e) => setConfig({ ...config, custoKwhAtual: parseFloat(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custo Hora Operador (R$)
              </label>
              <Input
                type="number"
                step="0.01"
                value={config.custoHoraOperador}
                onChange={(e) => setConfig({ ...config, custoHoraOperador: parseFloat(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Horas Úteis/Mês
              </label>
              <Input
                type="number"
                value={config.horasUteisMes}
                onChange={(e) => setConfig({ ...config, horasUteisMes: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Taxa Desperdício (%)
              </label>
              <Input
                type="number"
                step="0.01"
                value={config.taxaDesperdicioPadrao}
                onChange={(e) => setConfig({ ...config, taxaDesperdicioPadrao: parseFloat(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Margem Mínima (%)
              </label>
              <Input
                type="number"
                step="0.01"
                value={config.margemMinimaPadrao}
                onChange={(e) => setConfig({ ...config, margemMinimaPadrao: parseFloat(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custos Fixos Mensais (R$)
              </label>
              <Input
                type="number"
                step="0.01"
                value={config.custosFixosMensais}
                onChange={(e) => setConfig({ ...config, custosFixosMensais: parseFloat(e.target.value) })}
              />
            </div>
          </div>
        </motion.div>

        {/* Segurança 2FA (NOVO) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${userMe?.twoFactorEnabled ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <ShieldCheck className={`w-5 h-5 ${userMe?.twoFactorEnabled ? 'text-emerald-600' : 'text-red-600'}`} />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Segurança da Conta (2FA)</h2>
              <p className="text-sm text-gray-500">
                {userMe?.twoFactorEnabled 
                  ? 'A autenticação em duas etapas está ativada. Sua conta está protegida.' 
                  : 'Ative a autenticação em duas etapas para proteger sua conta administrativa.'}
              </p>
            </div>
            <Button 
              variant={userMe?.twoFactorEnabled ? 'outline' : 'nuvem'} 
              onClick={handleSetup2FA}
              disabled={userMe?.twoFactorEnabled}
            >
              {userMe?.twoFactorEnabled ? 'Configurado' : 'Configurar 2FA'}
            </Button>
          </div>
        </motion.div>

        {/* Modal 2FA */}
        {show2FAModal && setup2FAData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Configurar 2FA</h3>
                <p className="text-sm text-gray-500 mt-2">
                  Escaneie o QR Code abaixo com seu app de autenticação (Google Authenticator, Authy, etc).
                </p>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-inner flex justify-center mb-6">
                <img src={setup2FAData.qrCode} alt="2FA QR Code" className="w-48 h-48" />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                    Digite o código gerado
                  </label>
                  <Input 
                    type="text" 
                    placeholder="000 000"
                    maxLength={6}
                    value={token2fa}
                    onChange={(e) => setToken2fa(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                  />
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => setShow2FAModal(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    variant="nuvem" 
                    className="flex-1" 
                    onClick={confirmarAtivacao2FA}
                    disabled={token2fa.length < 6 || activating}
                    isLoading={activating}
                  >
                    Ativar 2FA
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Configuracoes;
